import { DemostackLogo } from './DemostackLogo';

interface MethodologyProps {
  onClose: () => void;
}

export function Methodology({ onClose }: MethodologyProps) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-ds-dark to-ds-dark-lighter text-gray-300">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold">
                <span className="text-ds-primary">fast</span>
                <span className="text-gray-400">.demo.link</span>
              </h1>
              <span className="px-2 py-0.5 text-xs font-medium bg-ds-accent/20 text-ds-accent rounded">
                Diagnostic
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs text-gray-500">powered by</span>
              <DemostackLogo className="h-3 text-gray-400" />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors"
          >
            ← Back to Test
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
        {/* Introduction */}
        <section className="mb-16">
          <p className="text-lg text-gray-400 leading-relaxed">
            Internet speed is more than just a single number. A connection with
            high bandwidth but poor latency under load will feel sluggish. Our
            test measures multiple dimensions to give you a complete picture of
            your connection quality.
          </p>
        </section>

        {/* What We Measure */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            What We Measure
          </h2>

          <div className="space-y-8">
            {/* Throughput */}
            <MetricCard
              title="Throughput"
              subtitle="Data transfer speed across tests"
              description="Each diagnostic test measures throughput in different conditions—sequential downloads, parallel connections, small file bursts, and sustained transfers. This reveals how your network performs under various real-world scenarios."
              details={[
                'Test A: Sequential baseline (single connection)',
                'Test B: Parallel downloads (6 concurrent connections)',
                'Test C: Small file bursts (15 files per wave)',
                'Test D: Sustained transfer (30 seconds continuous)',
              ]}
              color="blue"
            />

            {/* TTFB */}
            <MetricCard
              title="Time to First Byte (TTFB)"
              subtitle="Server response time"
              description="TTFB measures how long it takes for the first byte of data to arrive after requesting a file. This captures DNS lookup, TCP connection, TLS handshake, and server processing time."
              details={[
                'Critical for web page load performance',
                'Lower TTFB = faster initial page rendering',
                'Affected by network path and server location',
                'Lower is better: <100ms excellent, <300ms good',
              ]}
              color="green"
            />

            {/* Connection Overhead */}
            <MetricCard
              title="Connection Overhead"
              subtitle="DNS, TCP, and TLS timing"
              description="We measure each component of connection establishment: DNS lookup, TCP handshake, and TLS negotiation. These metrics reveal where connection delays occur."
              details={[
                'DNS: Domain name resolution time',
                'TCP: Connection establishment time',
                'TLS: Secure handshake negotiation time',
                '0ms values indicate connection reuse (HTTP/2)',
              ]}
              color="yellow"
            />

            {/* Jitter */}
            <MetricCard
              title="Jitter"
              subtitle="Latency consistency"
              description="Jitter measures how much your latency varies from moment to moment. High jitter causes choppy video calls and inconsistent gaming performance, even with good average latency."
              details={[
                'Calculated as standard deviation of latency samples',
                'Critical for real-time applications',
                '<10ms excellent for video calls',
                '>50ms will cause noticeable quality issues',
              ]}
              color="purple"
            />
          </div>
        </section>

        {/* Why These Metrics Matter */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            Why These Metrics Matter
          </h2>

          <div className="bg-ds-dark-lighter/50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">
              The Bufferbloat Problem
            </h3>
            <p className="text-gray-400 mb-4">
              Many users have "fast" connections that feel slow. The culprit is
              often bufferbloat, excessive buffering in routers and modems that
              causes latency to spike under load.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <ComparisonCard
                title="Good Connection"
                bandwidth="500 Mbps"
                unloaded="20ms"
                loaded="35ms"
                verdict="Excellent for all uses"
                status="good"
              />
              <ComparisonCard
                title="Bufferbloated Connection"
                bandwidth="500 Mbps"
                unloaded="20ms"
                loaded="800ms"
                verdict="Video calls will stutter"
                status="bad"
              />
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Both connections show 500 Mbps on a basic speed test, but the
              second one will have terrible real-world performance.
            </p>
          </div>

          <div className="bg-ds-dark-lighter/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Bandwidth vs Latency: What Matters When?
            </h3>
            <div className="space-y-3">
              <UseCaseRow
                activity="Video streaming (Netflix, YouTube)"
                bandwidth="High"
                latency="Low"
                note="Needs sustained throughput"
              />
              <UseCaseRow
                activity="Video calls (Zoom, Meet)"
                bandwidth="Low"
                latency="Critical"
                note="Real-time = latency sensitive"
              />
              <UseCaseRow
                activity="Web browsing"
                bandwidth="Medium"
                latency="Medium"
                note="Many small requests"
              />
              <UseCaseRow
                activity="Online gaming"
                bandwidth="Low"
                latency="Critical"
                note="Jitter matters most"
              />
              <UseCaseRow
                activity="Large file downloads"
                bandwidth="Critical"
                latency="Low"
                note="Pure throughput"
              />
              <UseCaseRow
                activity="Loading demos"
                bandwidth="High"
                latency="Medium"
                note="Mix of assets"
              />
            </div>
          </div>
        </section>

        {/* How Parallel Testing Works */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            How Parallel Testing Works
          </h2>

          <div className="bg-ds-dark-lighter/50 rounded-xl p-6">
            <p className="text-gray-400 mb-6">
              A single TCP connection can't saturate a fast internet link due to
              how TCP works. TCP starts slow and gradually increases speed
              ("slow start"), and is limited by round-trip time. Multiple
              parallel connections overcome these limitations.
            </p>

            <div className="space-y-4 font-mono text-sm">
              <div>
                <div className="text-gray-500 mb-1">
                  Single Connection (limited by TCP):
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500/30 h-4 w-24 rounded" />
                  <span className="text-gray-400">~80 Mbps</span>
                </div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">
                  6 Parallel Connections (saturates link):
                </div>
                <div className="space-y-1">
                  {[1, 2, 3, 4, 5, 6].map((connectionIndex) => (
                    <div
                      key={connectionIndex}
                      className="bg-blue-500/30 h-3 rounded"
                      style={{ width: `${60 + Math.random() * 40}%` }}
                    />
                  ))}
                </div>
                <div className="text-gray-400 mt-1">Combined: ~500 Mbps</div>
              </div>
            </div>

            <p className="text-gray-500 text-sm mt-6">
              This is the same technique used by fast.com, Speedtest.net, and
              other major speed testing services.
            </p>
          </div>
        </section>

        {/* Page Load Test */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">Page Load Test</h2>

          <p className="text-gray-400 mb-6">
            The optional page load test simulates loading a real web page with
            multiple assets. Unlike the bandwidth test, this measures realistic
            performance including:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <DetailCard
              title="Connection Overhead"
              items={[
                'DNS lookup time',
                'TCP connection establishment',
                'TLS/SSL handshake',
                'Time to first byte (TTFB)',
              ]}
            />
            <DetailCard
              title="Asset Loading"
              items={[
                'Sequential and parallel requests',
                'Different file types and sizes',
                'Browser-like behavior',
                'Cache simulation',
              ]}
            />
          </div>

          <p className="text-gray-500 text-sm">
            This test requires the Timing-Allow-Origin header from our server to
            access detailed timing information via the Resource Timing API.
          </p>
        </section>

        {/* Interpreting Results */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            Interpreting Your Results
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-center py-3 px-4 text-gray-400 font-medium">
                    Metric
                  </th>
                  <th className="text-center py-3 px-4 text-green-400 font-medium">
                    Excellent
                  </th>
                  <th className="text-center py-3 px-4 text-yellow-400 font-medium">
                    Good
                  </th>
                  <th className="text-center py-3 px-4 text-orange-400 font-medium">
                    Fair
                  </th>
                  <th className="text-center py-3 px-4 text-red-400 font-medium">
                    Poor
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4">Throughput (Baseline)</td>
                  <td className="py-3 px-4">{'>'} 500 Mbps</td>
                  <td className="py-3 px-4">100-500 Mbps</td>
                  <td className="py-3 px-4">25-100 Mbps</td>
                  <td className="py-3 px-4">{'<'} 25 Mbps</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4">TTFB</td>
                  <td className="py-3 px-4">{'<'} 50ms</td>
                  <td className="py-3 px-4">50-150ms</td>
                  <td className="py-3 px-4">150-300ms</td>
                  <td className="py-3 px-4">{'>'} 300ms</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4">DNS Lookup</td>
                  <td className="py-3 px-4">{'<'} 20ms</td>
                  <td className="py-3 px-4">20-50ms</td>
                  <td className="py-3 px-4">50-100ms</td>
                  <td className="py-3 px-4">{'>'} 100ms</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4">TLS Handshake</td>
                  <td className="py-3 px-4">{'<'} 50ms</td>
                  <td className="py-3 px-4">50-100ms</td>
                  <td className="py-3 px-4">100-200ms</td>
                  <td className="py-3 px-4">{'>'} 200ms</td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Jitter</td>
                  <td className="py-3 px-4">{'<'} 5ms</td>
                  <td className="py-3 px-4">5-15ms</td>
                  <td className="py-3 px-4">15-30ms</td>
                  <td className="py-3 px-4">{'>'} 30ms</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Diagnostic Test Suite */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            Diagnostic Test Suite (4 Tests)
          </h2>

          <p className="text-gray-400 mb-8">
            The full diagnostic runs four specialized tests designed to identify
            specific network issues that a simple speed test might miss. Each
            test targets a different aspect of network performance.
          </p>

          {/* Test A - Detailed */}
          <div className="mb-8 bg-ds-dark-lighter/30 rounded-xl border border-ds-primary/30 overflow-hidden">
            <div className="bg-ds-primary/10 px-6 py-4 border-b border-ds-primary/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-ds-primary">A</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Single Connection Baseline
                  </h3>
                  <p className="text-sm text-gray-400">
                    Establishes raw performance baseline
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-ds-primary mb-2">
                    What It Does
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Downloads 40 files sequentially using a single connection.
                    Uses 10MB files for consistent measurement. Each download
                    must complete before the next one starts.
                  </p>
                  <h4 className="text-sm font-medium text-ds-primary mb-2">
                    Why It Matters
                  </h4>
                  <p className="text-gray-400 text-sm">
                    This establishes your baseline performance without any
                    parallel acceleration. It shows what a single TCP connection
                    can achieve, limited by TCP slow-start and round-trip time.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-ds-primary mb-2">
                    Metrics Captured
                  </h4>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• DNS lookup time per request</li>
                    <li>• TCP connection establishment time</li>
                    <li>• TLS handshake duration</li>
                    <li>• Time to First Byte (TTFB)</li>
                    <li>• Download throughput per file</li>
                    <li>• Total test duration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Test B - Detailed */}
          <div className="mb-8 bg-ds-dark-lighter/30 rounded-xl border border-ds-accent/30 overflow-hidden">
            <div className="bg-ds-accent/10 px-6 py-4 border-b border-ds-accent/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-ds-accent">B</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Parallel Downloads
                  </h3>
                  <p className="text-sm text-gray-400">
                    Detects connection limits and proxy issues
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-ds-accent mb-2">
                    What It Does
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Downloads 144 files using 12 parallel connections. Files
                    include 10MB, 25MB, 50MB, and 100MB chunks. Downloads run
                    simultaneously in batches.
                  </p>
                  <h4 className="text-sm font-medium text-ds-accent mb-2">
                    Why It Matters
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Parallel downloads should be faster than sequential because
                    multiple TCP streams can better utilize available bandwidth.
                    If parallel is slower, something is limiting connections.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-ds-accent mb-2">
                    Issues Detected
                  </h4>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Connection limits:
                        </strong>{' '}
                        VPNs/proxies may restrict concurrent connections
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Proxy bottlenecks:
                        </strong>{' '}
                        Corporate proxies often can't handle parallel well
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">QoS policies:</strong>{' '}
                        Some networks deprioritize bulk downloads
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-ds-dark/50 rounded-lg text-xs text-gray-500">
                    <strong className="text-gray-400">Detection:</strong> If
                    Test B throughput is {'<'}70% of Test A, connection limits
                    are likely present.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Test C - Detailed */}
          <div className="mb-8 bg-ds-dark-lighter/30 rounded-xl border border-ds-secondary/30 overflow-hidden">
            <div className="bg-ds-secondary/10 px-6 py-4 border-b border-ds-secondary/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-ds-secondary">C</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Small Files Burst
                  </h3>
                  <p className="text-sm text-gray-400">
                    Detects inspection overhead and TLS costs
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-ds-secondary mb-2">
                    What It Does
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Downloads 300 small files ({'<'}100KB each) in 10 waves.
                    Each wave fires 30 files simultaneously, with 2-second
                    intervals between waves to detect inspection overhead.
                  </p>
                  <h4 className="text-sm font-medium text-ds-secondary mb-2">
                    Why It Matters
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Small files are dominated by per-request overhead, not
                    bandwidth. High TTFB on small files indicates inspection or
                    processing delays that affect real web browsing.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-ds-secondary mb-2">
                    Issues Detected
                  </h4>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          SSL/TLS inspection:
                        </strong>{' '}
                        Corporate firewalls decrypting traffic
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Content filtering:
                        </strong>{' '}
                        Security software scanning each request
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Antivirus scanning:
                        </strong>{' '}
                        Real-time file inspection adding latency
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-ds-dark/50 rounded-lg text-xs text-gray-500">
                    <strong className="text-gray-400">Detection:</strong> If
                    Test C TTFB is {'>'}50% higher than Test B TTFB, per-request
                    inspection overhead is present.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Test D - Detailed */}
          <div className="mb-8 bg-ds-dark-lighter/30 rounded-xl border border-pink-500/30 overflow-hidden">
            <div className="bg-pink-500/10 px-6 py-4 border-b border-pink-500/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-pink-400">D</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Sustained Transfer
                  </h3>
                  <p className="text-sm text-gray-400">
                    Detects throttling and rate limiting
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-pink-400 mb-2">
                    What It Does
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Downloads continuously for 60 seconds using 4 parallel
                    connections. Uses large files (25MB, 50MB, 100MB) to
                    maintain sustained throughput.
                  </p>
                  <h4 className="text-sm font-medium text-pink-400 mb-2">
                    Why It Matters
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Some ISPs and networks allow fast initial speeds but
                    throttle after detecting sustained bulk transfers. This test
                    reveals speed degradation over time.
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-pink-400 mb-2">
                    Issues Detected
                  </h4>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          ISP throttling:
                        </strong>{' '}
                        Speed drops after initial burst
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Rate limiting:
                        </strong>{' '}
                        Network policies capping sustained transfers
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">⚠</span>
                      <span>
                        <strong className="text-gray-400">
                          Data caps active:
                        </strong>{' '}
                        Carrier imposing soft limits
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-ds-dark/50 rounded-lg text-xs text-gray-500">
                    <strong className="text-gray-400">Detection:</strong> If
                    second half speed is {'<'}70% of first half speed,
                    throttling is detected.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How We Analyze */}
          <div className="bg-ds-dark-lighter/50 rounded-xl p-6 border border-ds-dark-border mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">
              How We Analyze Results
            </h3>
            <p className="text-gray-400 mb-4">
              After all 4 tests complete, we compare results to identify
              specific issues:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-ds-dark/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">
                  Test A vs Test B
                </h4>
                <p className="text-xs text-gray-500">
                  Compares single vs parallel to detect connection limits
                </p>
              </div>
              <div className="bg-ds-dark/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">
                  Test B vs Test C
                </h4>
                <p className="text-xs text-gray-500">
                  Compares parallel TTFB to burst TTFB to detect inspection
                  overhead
                </p>
              </div>
              <div className="bg-ds-dark/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">
                  Test D First vs Last
                </h4>
                <p className="text-xs text-gray-500">
                  Compares start vs end speeds to detect throttling over time
                </p>
              </div>
            </div>
          </div>

          <div className="bg-ds-dark-lighter/50 rounded-xl p-6 border border-ds-dark-border">
            <h3 className="text-lg font-semibold text-white mb-4">
              About Connection Timing (DNS/TCP/TLS = 0ms)
            </h3>
            <p className="text-gray-400 mb-4">
              You may notice DNS, TCP, and TLS times showing 0ms in the detailed
              results. This is{' '}
              <span className="text-ds-accent">expected behavior</span> due to
              HTTP/2 connection reuse.
            </p>
            <p className="text-gray-500 text-sm mb-4">
              Modern browsers establish a single persistent connection to each
              server and multiplex all requests over it. Only the first request
              incurs connection overhead; subsequent requests reuse the existing
              connection.
            </p>
            <div className="text-sm text-gray-500">
              <span className="text-gray-400">What this means:</span>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your connection to our servers is healthy and efficient</li>
                <li>HTTP/2 multiplexing is working correctly</li>
                <li>TTFB (Time to First Byte) shows actual request latency</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Technical Details */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8">
            Technical Details
          </h2>

          <div className="bg-ds-dark-lighter/50 rounded-xl p-6 space-y-4 text-sm border border-ds-dark-border">
            <DetailRow
              label="Test Server"
              value="Cloudflare Workers (edge network)"
            />
            <DetailRow
              label="Test Files"
              value="Cloudflare R2 (global distribution)"
            />
            <DetailRow
              label="Test A (Baseline)"
              value="40 files, sequential"
            />
            <DetailRow
              label="Test B (Parallel)"
              value="60 files, 6 concurrent"
            />
            <DetailRow
              label="Test C (Burst)"
              value="75 files (15 per wave × 5)"
            />
            <DetailRow
              label="Test D (Sustained)"
              value="30 seconds continuous"
            />
            <DetailRow
              label="Speed Calculation"
              value="2-second sliding window average"
            />
            <DetailRow
              label="Protocol"
              value="HTTPS with Timing-Allow-Origin"
            />
          </div>
        </section>

        {/* CTA */}
        <div className="pt-8 border-t border-ds-dark-border text-center">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-ds-primary hover:bg-ds-primary-dark rounded-lg font-medium text-white transition-colors"
          >
            Run Speed Test
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 sm:p-6 text-center text-xs sm:text-sm text-gray-500">
        <p>Network Performance Diagnostic Tool</p>
        <div className="mt-1 flex items-center justify-center gap-1">
          <span>Powered by</span>
          <DemostackLogo className="h-3 text-gray-400" />
        </div>
      </footer>
    </div>
  );
}

// Helper Components

interface MetricCardProps {
  title: string;
  subtitle: string;
  description: string;
  details: string[];
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

function MetricCard({
  title,
  subtitle,
  description,
  details,
  color,
}: MetricCardProps) {
  const colorClasses = {
    blue: 'border-blue-500/30 bg-blue-500/5',
    green: 'border-green-500/30 bg-green-500/5',
    yellow: 'border-yellow-500/30 bg-yellow-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
  };

  const dotColors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
  };

  return (
    <div className={`rounded-xl border p-6 ${colorClasses[color]}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-3 h-3 rounded-full mt-1.5 ${dotColors[color]}`} />
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      <p className="text-gray-400 mb-4">{description}</p>
      <ul className="space-y-2">
        {details.map((detail) => (
          <li
            key={detail}
            className="flex items-start gap-2 text-sm text-gray-500"
          >
            <span className="text-gray-600 mt-0.5">•</span>
            <span>{detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ComparisonCardProps {
  title: string;
  bandwidth: string;
  unloaded: string;
  loaded: string;
  verdict: string;
  status: 'good' | 'bad';
}

function ComparisonCard({
  title,
  bandwidth,
  unloaded,
  loaded,
  verdict,
  status,
}: ComparisonCardProps) {
  const borderColor =
    status === 'good' ? 'border-green-500/30' : 'border-red-500/30';
  const verdictColor = status === 'good' ? 'text-green-400' : 'text-red-400';

  return (
    <div className={`rounded-lg border p-4 ${borderColor}`}>
      <div className="text-white font-medium mb-3">{title}</div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Bandwidth:</span>
          <span className="text-gray-300">{bandwidth}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Latency (idle):</span>
          <span className="text-gray-300">{unloaded}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Latency (loaded):</span>
          <span className="text-gray-300">{loaded}</span>
        </div>
      </div>
      <div className={`mt-3 text-sm font-medium ${verdictColor}`}>
        {verdict}
      </div>
    </div>
  );
}

interface UseCaseRowProps {
  activity: string;
  bandwidth: string;
  latency: string;
  note: string;
}

function UseCaseRow({ activity, bandwidth, latency, note }: UseCaseRowProps) {
  const importanceColor = (importance: string) => {
    if (importance === 'Critical') return 'text-red-400';
    if (importance === 'High') return 'text-yellow-400';
    if (importance === 'Medium') return 'text-blue-400';
    return 'text-gray-400';
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-700/50 last:border-0">
      <div className="flex-1">
        <span className="text-gray-300">{activity}</span>
        <span className="text-gray-600 text-sm ml-2">({note})</span>
      </div>
      <div className="flex gap-6 text-sm">
        <div>
          <span className="text-gray-500">BW: </span>
          <span className={importanceColor(bandwidth)}>{bandwidth}</span>
        </div>
        <div>
          <span className="text-gray-500">Lat: </span>
          <span className={importanceColor(latency)}>{latency}</span>
        </div>
      </div>
    </div>
  );
}

interface DetailCardProps {
  title: string;
  items: string[];
}

function DetailCard({ title, items }: DetailCardProps) {
  return (
    <div className="bg-ds-dark-lighter/50 rounded-lg p-4">
      <h4 className="text-white font-medium mb-3">{title}</h4>
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item}
            className="text-sm text-gray-400 flex items-center gap-2"
          >
            <span className="text-gray-600">→</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-300">{value}</span>
    </div>
  );
}

interface DiagnosticTestCardProps {
  testId: string;
  title: string;
  description: string;
  color: 'primary' | 'accent' | 'secondary' | 'pink';
}

function DiagnosticTestCard({
  testId,
  title,
  description,
  color,
}: DiagnosticTestCardProps) {
  const colorClasses = {
    primary: 'border-ds-primary/30 text-ds-primary',
    accent: 'border-ds-accent/30 text-ds-accent',
    secondary: 'border-ds-secondary/30 text-ds-secondary',
    pink: 'border-ds-pink/30 text-ds-pink',
  };

  return (
    <div
      className={`rounded-xl border p-4 bg-ds-dark-lighter/30 ${colorClasses[color].split(' ')[0]}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`font-bold text-lg ${colorClasses[color].split(' ')[1]}`}
        >
          {testId}
        </span>
        <div>
          <h4 className="font-medium text-white">{title}</h4>
          <p className="text-sm text-gray-400 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}
